import React, { useState } from 'react';
import { Box, Typography, Button, Container, useTheme } from '@mui/material';
import { Link } from "react-router-dom";

const sections = [
    {
        bgColor: 'rgba(255, 255, 255, 0.7)',
        clip: 'polygon(0 0, 100% 5%, 100% 100%, 0 95%)',

        description: 'Our CTMS tool allows you to easily plan, manage, and track your clinical trial portfolio within budget. Designed to intuitively manage and logically structure data to enable efficient collaboration and reporting, our validated cloud-based CTMS enables teams to optimize workflows producing real-time insight-driven data and analytics. It empowers users to speed up clinical trials and reduce drug time-to-market by managing data and ensuring compliance. Whether you are conducting a single study or have a large portfolio, our robust CTMS is scalable to effectively meet your needs.',
        showButton: false,
    },
    {
        image: '/insightsgrid.png',
        bgColor: 'rgba(208, 147, 239, 0.2)',
        clip: 'polygon(0 5%, 100% 0, 100% 95%, 0 100%)',
        title: 'FEATURES & BENEFITS',
        paragraphs: [
            <>
                <strong>Comprehensive functionality</strong> – everything you need is in one software application – from study startup, to execution, through closeout: along with flexible reporting, site and investigator management, and a complete, workflow driven Monitoring Module that now support Decentralized Clinical Trials (DCT). Intrinsic CTMS is used by both Sponsors and CROs, globally.
            </>,
            <>
                <strong>Easy to Use</strong> — the intuitive interface and the intelligent functions in Intrinsic’s CTMS makes the users’ daily work easier and more efficient, and it’s a product that customers say they actually enjoy using.
            </>,
            <>
                <strong>Easy to Implement and Integrate</strong> – standardizations and proven processes make Intrinsic’s CTMS available for new customers within a very short time. Our team supports the customer throughout the entire process – from initial configuration through rollout. As the central hub and backbone for your clinical trials management, our CTMS enables easy integration with other clinical systems. Standard interfaces are available to external eTMF, EDC, portfolio and project management tools, clinical data warehouses, active directory for single-sign-on, and more.
            </>,
            <>
                <strong>Low Operational Costs</strong> – because of the open software architecture, future updates to new standard versions future will be included in the monthly SaaS fee at no additional cost and with no dependencies on vendors.
            </>,
            <>
                <strong>Meeting Industry Qualifications</strong> – using our thorough data security processes, system validation and quality assurance processes, Intrinsic’s CTMS complies with international industry standards like 21 CFR Part 11, ICH GCP and EU GDPR.
            </>
        ],

    },
    {
        bgColor: 'rgba(255, 255, 255, 0.7)',
        clip: 'polygon(0 0, 100% 5%, 100% 100%, 0 95%)',
        title: 'READY TO SEE MORE?',
        description: 'Contact us today and discover how Intrinsic CTMS can accelerate your research.',
        showButton: true,
    },
];

const IntrinsicCtms = () => {
    const [showVideo, setShowVideo] = useState(false);
    const theme = useTheme();
    //const headingColor = theme.palette.mode === 'light' ? '#9e9e9e' : '#797373ff';

    return (
        <>
            <Box sx={{ py: 2, px: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography

                    sx={{
                        fontWeight: 'bold',
                        color: theme.palette.mode === "dark" ? "white" : "black",
                        fontSize: '1.5rem',
                        mb: 2,
                    }}
                >
                    INTRINSIC CTMS
                </Typography>
                <Typography
                    variant="h4"
                    sx={{
                        mb: 2,
                        fontSize: '1.2rem',
                        textAlign: 'center',
                        maxWidth: '800px',
                    }}
                >
                    Seamlessly coordinate your clinical trial data by using a centralized tool to communicate and ensure compliance throughout the study lifecycle
                </Typography>


                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        justifyContent: { xs: 'center', md: 'flex-end' },
                        maxWidth: '100%',
                    }}
                >
                    {!showVideo ? (
                        <img
                            src="/ctms-video-imag.png" // thumbnail image
                            alt="Click to play"
                            onClick={() => setShowVideo(true)}
                            style={{
                                cursor: 'pointer',
                                borderRadius: '12px',
                                maxWidth: '500px',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                                width: '100%',
                                height: '300px',
                            }}
                        />
                    ) : (
                        <iframe
                            width="500"
                            height="300"
                            style={{
                                borderRadius: '12px',
                                maxWidth: '500px',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                            }}
                            src="https://www.youtube.com/embed/xlt-KyMLwa4?autoplay=1" // ✅ YouTube embed link
                            title="CTMS Video"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        ></iframe>
                    )}

                </Box>
            </Box>

            {sections.map((section, index) => (
                <Box
                    key={index}
                    sx={{
                        backgroundColor: section.bgColor,
                        clipPath: {
                            xs: 'none', // remove clipPath on small screens to prevent text cutting
                            md:
                                index % 2 === 0
                                    ? 'polygon(0 0, 100% 20%, 100% 100%, 0 80%)'
                                    : 'polygon(0 20%, 100% 0, 100% 80%, 0 100%)',
                        },
                        position: 'relative',
                        zIndex: 10 - index,
                        mt: index === 0 ? 0 : { xs: 0, md: '-80px' }, // remove negative margin for xs
                        py: { xs: 8, md: 12 }, // reduce vertical padding for small screens
                        px: { xs: 2, md: 6 },
                        borderRadius: { xs: 2, md: 0 }, // optional: rounded edges for small screens
                    }}
                >
                    <Container>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: {
                                    xs: 'column', // stack content vertically on small screens
                                    md: index % 2 === 0 ? 'row' : 'row-reverse',
                                },
                                alignItems: 'center',
                                gap: 4,
                            }}
                        >
                            <Box
                                sx={{
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    textAlign: 'left',
                                    px: { xs: 1, md: 8 }, // reduce padding on small screens
                                    overflow: 'hidden', // prevent text from overflowing
                                }}
                            >
                                <Typography
                                    variant="h5"
                                    sx={{
                                        fontWeight: 'bold',
                                        color: theme.palette.mode === 'dark' ? 'white' : 'black',
                                        mb: 4,
                                        fontSize: { xs: '1.3rem', md: '1.5rem' }, // responsive font size
                                    }}
                                >
                                    {section.title}
                                </Typography>

                                {section.showButton && (
                                    <Button
                                        variant="contained"
                                        component={Link}
                                        to="/request-demo"
                                        sx={{
                                            backgroundColor: '#9440dd',
                                            color: '#FFFFFF',
                                            px: '40px',
                                            py: '6px',
                                            fontSize: { xs: '1rem', md: '1.4rem' },
                                            borderRadius: '50px',
                                            textTransform: 'none',
                                            fontWeight: 500,
                                            boxShadow: '0px 4px 12px rgba(0, 174, 239, 0.4)',
                                            transition: 'transform 0.3s ease, background-color 0.3s ease',
                                            '&:hover': {
                                                backgroundColor: '#9440dd',
                                                transform: 'scale(1.05)',
                                            },
                                            mb: 4,
                                        }}
                                    >
                                        Request a Demo
                                    </Button>
                                )}

                                {section.paragraphs
                                    ? section.paragraphs.map((para, idx) => (
                                        <Typography
                                            key={idx}
                                            variant="body1"
                                            sx={{
                                                mb: 2,
                                                textAlign: 'justify',
                                                fontSize: { xs: '0.9rem', md: '1rem' },
                                                fontFamily: 'Montserrat, sans-serif',
                                            }}
                                        >
                                            {para}
                                        </Typography>
                                    ))
                                    : (
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                mb: section.showButton ? 4 : 0,
                                                textAlign: 'justify',
                                                px: { xs: 1, md: 4 },
                                                fontSize: { xs: '0.9rem', md: '1rem' },
                                                fontFamily: 'Montserrat, sans-serif',
                                            }}
                                        >
                                            {section.description}
                                        </Typography>
                                    )}
                            </Box>
                        </Box>
                    </Container>
                </Box>
            ))}

        </>
    );
};

export default IntrinsicCtms;


